/* eslint-disable jsx-a11y/alt-text */
import React, { useRef } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import { Box, Card, Stack, Link, Container, Typography, GlobalStyles } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PATH_AUTH } from '../../routes/paths';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Page from '../../components/Page';
import Logo from '../../components/Logo';
import { APP_NAME, GRADIENT_CONFIG } from '../../config';
// sections
import { LoginForm } from '../../sections/auth/login';
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  width: '100vw',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));
// Left side with gradient background
const GradientSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  background: `linear-gradient(135deg, ${GRADIENT_CONFIG.secondary} 50%, ${GRADIENT_CONFIG.primary} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    width: '50%',
  },
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    width: '100%',
    height: '100vh',
    zIndex: 0,
  },
}));

// Right side with white background
const FormSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    width: '50%',
    backgroundColor: theme.palette.background.paper,
  },
  [theme.breakpoints.down('md')]: {
    backgroundColor: 'transparent',
    zIndex: 1, // Higher z-index to show above gradient
    padding: theme.spacing(2),
    margin: '0 auto',
    maxWidth: 500, // Limit max width for better centering on tablets
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'absolute',
  top: -40, // Move up to blend with form card
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 20,
  '& .logo-container': {
    width: 80,
    height: 80,
    background: '#ffffff', // White background to ensure visibility
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 12px 32px rgba(255,107,53,0.3), 0 4px 16px rgba(0,0,0,0.1)',
    position: 'relative',
    border: `3px solid ${GRADIENT_CONFIG.primary}`, // Colored border instead of white
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -3,
      left: -3,
      right: -3,
      bottom: -3,
      background: `linear-gradient(135deg, ${GRADIENT_CONFIG.primary}, ${GRADIENT_CONFIG.secondary})`,
      borderRadius: 23,
      zIndex: -1,
    },
  },
}));

const FormCard = styled(Card)(({ theme }) => ({
  maxWidth: 400,
  width: '100%',
  borderRadius: 24,
  background: theme.palette.background.paper,
  boxShadow: theme.customShadows.card,
  padding: theme.spacing(4, 3, 3),
  position: 'relative',
  margin: '0 auto',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'visible',
  zIndex: 1,
  color: theme.palette.text.primary,
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
  },
  [theme.breakpoints.down('md')]: {
    background: theme.palette.background.paper,
    backdropFilter: 'none',
    boxShadow: theme.shadows[10],
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3, 2.5, 2.5),
    margin: '60px auto',
  },
  [theme.breakpoints.down('sm')]: {
    margin: '40px auto',
    padding: theme.spacing(3, 2, 2),
  },
}));

const StyledLoginForm = styled('div')(({ theme }) => ({
  width: '100%',
  '& .MuiTextField-root': {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'light' ? '#f8f9fa' : theme.palette.grey[800],
      borderRadius: 12,
      '& fieldset': {
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      [theme.breakpoints.down('md')]: {
        backgroundColor: theme.palette.mode === 'light' ? '#f8f9fa' : theme.palette.grey[800],
        backdropFilter: 'blur(10px)',
        '& fieldset': {
          borderColor: theme.palette.divider,
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: '#6c757d',
      '&.Mui-focused': {
        color: GRADIENT_CONFIG.primary,
      },
      [theme.breakpoints.down('md')]: {
        color: 'rgba(0, 0, 0, 0.7)', // Darker label color for better visibility
      },
    },
  },
  '& .MuiButton-root': {
    borderRadius: 12,
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    padding: theme.spacing(1.5, 3),
    background: `linear-gradient(135deg, ${GRADIENT_CONFIG.primary} 0%, ${GRADIENT_CONFIG.secondary} 100%)`,
    color: 'white',
    boxShadow: `0 4px 16px rgba(255, 107, 53, 0.3)`,
    border: 'none',
    transition:
      'background 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1)',
    '&:hover': {
      background: `linear-gradient(135deg, ${GRADIENT_CONFIG.secondary} 0%, ${GRADIENT_CONFIG.primary} 100%)`,
      boxShadow: `0 6px 20px rgba(255, 107, 53, 0.4)`,
      transform: 'scale(1.05) translateY(-2px)',
    },
  },
}));

const AnimatedBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'hidden',
  zIndex: 0,
  '& .floating-shape': {
    position: 'absolute',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    animation: 'float 6s ease-in-out infinite',
  },
  '& .shape-1': {
    width: 100,
    height: 100,
    top: '20%',
    left: '15%',
    animationDelay: '0s',
  },
  '& .shape-2': {
    width: 150,
    height: 150,
    top: '60%',
    right: '15%',
    animationDelay: '2s',
  },
  '& .shape-3': {
    width: 80,
    height: 80,
    top: '80%',
    left: '25%',
    animationDelay: '4s',
  },
  '& .shape-4': {
    width: 60,
    height: 60,
    top: '30%',
    right: '30%',
    animationDelay: '1s',
    background: 'rgba(255, 255, 255, 0.15)',
  },
  '& .shape-5': {
    width: 120,
    height: 120,
    top: '10%',
    right: '20%',
    animationDelay: '3s',
    background: 'rgba(255, 255, 255, 0.08)',
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px) rotate(0deg)',
    },
    '50%': {
      transform: 'translateY(-20px) rotate(180deg)',
    },
  },
}));

// Beautiful content for left side (desktop)
const WelcomeContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  textAlign: 'center',
  color: 'white',
  maxWidth: 400,
  padding: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    display: 'none', // Hide on mobile
  },
}));

const FeatureGrid = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '10%',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(2),
  width: '80%',
  maxWidth: 300,
  [theme.breakpoints.down('md')]: {
    display: 'none', // Hide on mobile
  },
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  padding: theme.spacing(2),
  border: '1px solid rgba(255, 255, 255, 0.2)',
  textAlign: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.15)',
    transform: 'translateY(-4px)',
  },
}));

const WatermarkContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 20,
  right: 20,
  zIndex: 10,
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(10px)',
  padding: theme.spacing(1, 2),
  borderRadius: 8,
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const MobileFormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 400,
  position: 'relative',
  zIndex: 2, // Ensure it's above everything
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(4),
  },
}));

const GeometricPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20%',
  left: '15%',
  width: 200,
  height: 200,
  zIndex: 2,
  '& .pattern-element': {
    position: 'absolute',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    animation: 'rotate 20s linear infinite',
  },
  '& .circle-1': {
    width: 80,
    height: 80,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  '& .circle-2': {
    width: 120,
    height: 120,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    animationDelay: '-5s',
    animationDirection: 'reverse',
  },
  '& .circle-3': {
    width: 160,
    height: 160,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    animationDelay: '-10s',
  },
  '@keyframes rotate': {
    '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
    '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
  },
}));

// ----------------------------------------------------------------------

export default function Login() {
  const theme = useTheme();
  const mdUp = useResponsive('up', 'md');

  return (
    <Page title="Login">
      <>
        <GlobalStyles
          styles={`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(180deg);
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 0.8;
            }
            50% {
              opacity: 1;
            }
          }
          @keyframes rotate {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }
          @keyframes slideUp {
            0% { 
              opacity: 0;
              transform: translateY(30px);
            }
            100% {
              opacity: 1;
              transform: translateY(0px);
            }
          }
        `}
        />
        <RootStyle>
          {/* Left Side - Gradient Background (Desktop Only) */}
          <GradientSection>
            <AnimatedBackground>
              <div className="floating-shape shape-1" />
              <div className="floating-shape shape-2" />
              <div className="floating-shape shape-3" />
              <div className="floating-shape shape-4" />
              <div className="floating-shape shape-5" />
            </AnimatedBackground>

            <GeometricPattern>
              <div className="pattern-element circle-1" />
              <div className="pattern-element circle-2" />
              <div className="pattern-element circle-3" />
            </GeometricPattern>

            <WelcomeContent>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: 'linear-gradient(45deg, #ffffff 0%, rgba(255,255,255,0.8) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'slideUp 1s ease-out',
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.9,
                  mb: 4,
                  lineHeight: 1.6,
                  animation: 'slideUp 1s ease-out 0.2s both',
                }}
              >
                Sign in to continue your journey with us and access all amazing features
              </Typography>

              {/* Statistics */}
              <Stack direction="row" spacing={4} justifyContent="center" sx={{ mb: 4 }}>
                <Box sx={{ textAlign: 'center', animation: 'slideUp 1s ease-out 0.4s both' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    10K+
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Happy Users
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', animation: 'slideUp 1s ease-out 0.6s both' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    99%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Satisfaction
                  </Typography>
                </Box>
              </Stack>
            </WelcomeContent>

            {/* Feature Cards */}
            <FeatureGrid>
              <FeatureCard sx={{ animation: 'slideUp 1s ease-out 0.8s both' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                  🚀
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Fast & Secure
                </Typography>
              </FeatureCard>
              <FeatureCard sx={{ animation: 'slideUp 1s ease-out 1s both' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                  💎
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Premium Quality
                </Typography>
              </FeatureCard>
              <FeatureCard sx={{ animation: 'slideUp 1s ease-out 1.2s both' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                  🎯
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Goal Focused
                </Typography>
              </FeatureCard>
              <FeatureCard sx={{ animation: 'slideUp 1s ease-out 1.4s both' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                  🌟
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Excellence
                </Typography>
              </FeatureCard>
            </FeatureGrid>

            <WatermarkContainer>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                }}
              >
                Developed By A D E Developers
              </Typography>
            </WatermarkContainer>
          </GradientSection>
          {/* Right Side - Login Form */}
          <FormSection>
            {/* Mobile Form Container */}
            <MobileFormContainer>
              <FormCard>
                {/* Logo positioned to blend with form card */}
                <LogoContainer>
                  <Box className="logo-container">
                    <Logo
                      sx={{
                        width: 70,
                        height: 70,
                        color: (theme) => (theme.palette.mode === 'dark' ? '#fff' : GRADIENT_CONFIG.primary),
                      }}
                    />
                  </Box>
                </LogoContainer>
                <Stack spacing={3} alignItems="center" sx={{ pt: 3 }}>
                  <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography
                      variant="h4"
                      gutterBottom
                      sx={{
                        color: 'rgba(0, 0, 0, 0.85)',
                        fontWeight: 700,
                        mb: 1,
                        [theme.breakpoints.down('md')]: {
                          textShadow: '0 2px 4px rgba(255,255,255,0.3)',
                        },
                      }}
                    >
                      Sign In to {APP_NAME}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontWeight: 400,
                        mb: 3,
                      }}
                    >
                      Log into your account to continue with the experience.
                    </Typography>
                  </Box>
                  <StyledLoginForm>
                    <LoginForm />
                  </StyledLoginForm>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      Don't have an account?
                    </Typography>
                    <Link
                      variant="subtitle2"
                      component={RouterLink}
                      to={PATH_AUTH.register}
                      sx={{
                        color: GRADIENT_CONFIG.primary,
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline',
                          color: GRADIENT_CONFIG.tertiary,
                        },
                      }}
                    >
                      Sign Up
                    </Link>
                  </Stack>
                </Stack>
              </FormCard>
            </MobileFormContainer>
          </FormSection>
        </RootStyle>
      </>
    </Page>
  );
}
